import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Plus, Trash2, Users, FileSpreadsheet, Pencil, CheckSquare, Square, Loader2, Search } from "lucide-react";
import { listCompanies, deleteCompany, bulkDeleteCompanies } from "@/lib/api";
import { toast } from "sonner";

export default function CompaniesList() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [query, setQuery] = useState("");

  // Substring search across name / CIN / type.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.cin || "").toLowerCase().includes(q) ||
      (c.company_type || "").toLowerCase().includes(q)
    );
  }, [companies, query]);

  const load = () => {
    setLoading(true);
    listCompanies().then((r) => setCompanies(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allOn = filtered.length > 0 && filtered.every((c) => selected[c.id]);
  const toggleAll = () => {
    const v = !allOn;
    const next = { ...selected };
    filtered.forEach((c) => { next[c.id] = v; });
    setSelected(next);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This also removes its resolutions & calendar.`)) return;
    try {
      await deleteCompany(id);
      toast.success("Company deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(
      `Delete ${selectedIds.length} compan${selectedIds.length === 1 ? "y" : "ies"}?\n` +
      `This also removes ALL associated resolutions and calendar events. This action cannot be undone.`
    )) return;
    setBulkDeleting(true);
    try {
      const res = await bulkDeleteCompanies(selectedIds);
      toast.success(`Deleted ${res.data?.deleted || 0} compan${(res.data?.deleted === 1) ? "y" : "ies"}`);
      setSelected({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="companies-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="label-eyebrow mb-2">Master Data</div>
          <h1 className="font-heading text-4xl font-light tracking-tight">Companies</h1>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="swiss-btn-ghost text-red-700 border-red-300 hover:bg-red-50"
              data-testid="bulk-delete-btn"
            >
              {bulkDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.length}`}
            </button>
          )}
          <Link to="/companies/import" className="swiss-btn-ghost" data-testid="bulk-import-button">
            <FileSpreadsheet size={14} /> Bulk Import
          </Link>
          <Link to="/companies/new" className="swiss-btn-primary" data-testid="add-company-button">
            <Plus size={16} /> Add Company
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 py-12 text-center" data-testid="companies-loading">Loading…</div>
      ) : companies.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 bg-zinc-50 p-16 text-center" data-testid="companies-empty">
          <Building2 size={32} strokeWidth={1.25} className="mx-auto text-zinc-400 mb-4" />
          <h2 className="font-heading text-xl font-medium mb-2">No companies yet</h2>
          <p className="text-zinc-500 mb-6 text-sm">Upload your MCA master data PDF to extract company and director details automatically.</p>
          <Link to="/companies/new" className="swiss-btn-primary" data-testid="add-company-empty-cta">
            <Plus size={16} /> Upload MCA Master Data
          </Link>
        </div>
      ) : (
        <div className="border border-zinc-200" data-testid="companies-list">
          <div className="px-6 py-3 border-b border-zinc-200 bg-zinc-50/60 flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, CIN or type…"
                className="w-full border border-zinc-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                data-testid="companies-search"
              />
            </div>
            <button onClick={toggleAll} className="flex items-center gap-2 text-zinc-600 hover:text-[#002FA7]" data-testid="select-all">
              {allOn ? <CheckSquare size={16} className="text-[#002FA7]" /> : <Square size={16} />}
              <span className="text-xs uppercase tracking-[0.15em]">Select All{query ? " (Filtered)" : ""}</span>
            </button>
            <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 ml-auto" data-testid="companies-count">
              {selectedIds.length > 0 ? `${selectedIds.length} selected · ` : ""}
              {query ? `${filtered.length} of ${companies.length}` : `${companies.length} total`}
            </div>
          </div>
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-zinc-500" data-testid="companies-no-match">
              No companies match <span className="font-mono-plex text-zinc-700">"{query}"</span>.
            </div>
          ) : (
            filtered.map((c) => (
            <div
              key={c.id}
              className={`grid grid-cols-[auto_1fr_auto] gap-4 p-6 border-t border-zinc-200 hover:bg-zinc-50/50 transition-colors ${selected[c.id] ? "bg-[#002FA7]/5" : ""}`}
              data-testid={`company-row-${c.id}`}
            >
              <input
                type="checkbox"
                checked={!!selected[c.id]}
                onChange={() => toggle(c.id)}
                className="accent-[#002FA7] mt-1.5"
                data-testid={`select-${c.id}`}
                aria-label={`Select ${c.name}`}
              />
              <Link to={`/companies/${c.id}`} className="block min-w-0">
                <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                  <h3 className="font-heading text-xl font-medium hover:text-[#002FA7]">{c.name}</h3>
                  <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-zinc-300 text-zinc-700">{c.company_type}</span>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-zinc-600">
                  <span className="font-mono-plex">{c.cin || "CIN: —"}</span>
                  <span className="flex items-center gap-1.5"><Users size={14} strokeWidth={1.5} /> {c.directors?.length || 0} directors</span>
                  {c.professionals?.length > 0 && (
                    <span className="text-zinc-500">{c.professionals.length} professional{c.professionals.length > 1 ? "s" : ""}</span>
                  )}
                </div>
              </Link>
              <div className="flex items-center gap-1">
                <Link to={`/generate?company=${c.id}`} className="text-sm text-[#002FA7] hover:underline px-2" data-testid={`generate-from-${c.id}`}>
                  Generate →
                </Link>
                <Link
                  to={`/companies/${c.id}/edit`}
                  className="text-zinc-400 hover:text-[#002FA7] p-2"
                  data-testid={`edit-company-${c.id}`}
                  aria-label="Edit"
                >
                  <Pencil size={15} />
                </Link>
                <button
                  onClick={() => handleDelete(c.id, c.name)}
                  className="text-zinc-400 hover:text-red-600 p-2"
                  data-testid={`delete-company-${c.id}`}
                  aria-label="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
          )}
        </div>
      )}
    </div>
  );
}
